import React, { useEffect, useState } from 'react';
import { Table, Button, Drawer, Typography, Spin, Tag, Space, Tooltip, Collapse, Empty } from 'antd';
import { EyeOutlined, FileTextOutlined, SoundOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import constants from '../../constants';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const InterviewTable = () => {
  const [data, setData] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      position: ["bottomCenter"],
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50'],
    },
    filters: {},
    sorter: {},
  });
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination.current, tableParams.pagination.pageSize, tableParams.filters, tableParams.sorter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { current, pageSize } = tableParams.pagination;
      const sortField = tableParams.sorter.field;
      const sortOrder = tableParams.sorter.order;
      
      let queryParams = `page=${current}&limit=${pageSize}`;
      
      if (sortField && sortOrder) {
        const order = sortOrder === 'ascend' ? 'asc' : 'desc';
        queryParams += `&sort=${sortField}:${order}`;
      }
      
      // Add filters if they exist
      Object.entries(tableParams.filters || {}).forEach(([key, values]) => {
        if (values && values.length > 0) {
          queryParams += `&${key}=${values.join(',')}`;
        }
      });

      const res = await axios.get(`${constants.NEW_DOMAIN}/api/v1/eval/getAllEvalData?${queryParams}`);
      const users = res.data?.data?.users || [];
      setData(users);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.data.results,
        },
      });
    } catch (error) {
      console.error('Failed to fetch interview data:', error);
    }
    setLoading(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter: {
        field: sorter.field,
        order: sorter.order
      },
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const openDrawer = (interviewData, record) => {
    setSelectedInterview(interviewData);
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const getStatusTag = (status) => {
    if (!status) return <Tag color="default">No Status</Tag>;
    
    const statusConfig = {
        EVALUATED: { color: 'success', icon: <CheckCircleOutlined /> },
        COMPLETED: { color: 'success', icon: <CheckCircleOutlined /> },
        INPROGRESS: { color: 'processing', icon: null },
        NOTSTARTED: { color: 'warning', icon: null },
    };
    
    const config = statusConfig[status.toLowerCase()] || { color: 'default', icon: null };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const formatScore = (score) => {
    if (score === undefined || score === null) return 'N/A';
    if (typeof score === 'number') {
      return score.toFixed(1);
    }
    return score;
  };

  const columns = [
    {
      title: 'Interview ID',
      dataIndex: '_id',
      key: '_id',
      width: 220,
      ellipsis: {
        showTitle: false,
      },
      render: id => (
        <Tooltip placement="topLeft" title={id}>
          {id}
        </Tooltip>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      width: 150,
      render: (_, record) => {
        const name = record.enrollmentId?.userId?.name || 'N/A';
        return (
          <Space>
            <UserOutlined />
            <Text>{name}</Text>
          </Space>
        );
      },
      sorter: true,
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const contact = record.enrollmentId?.userId?.email || record.enrollmentId?.userId?.phone || 'N/A';
        return (
          <Tooltip title={contact}>
            {contact}
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => getStatusTag(record.enrollmentId?.progress?.interview?.status),
    },
    {
      title: 'Score',
      key: 'score',
      width: 100,
      sorter: true,
      render: (_, record) => {
        const score = record.enrollmentId?.progress?.interview?.score;
        return formatScore(score);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<EyeOutlined />}
          onClick={() => openDrawer(record.raw_interview, record)}
        >
          View
        </Button>
      ),
    },
  ];

  // Format the feedback text for readability
  const formatFeedback = (text) => {
    if (!text) return null;
    // Split by common separators and create paragraphs
    return text.split(/\n|\.(?=\s|$)/).filter(Boolean).map((paragraph, idx) => (
      <Paragraph key={idx}>{paragraph.trim()}.</Paragraph>
    ));
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Interview Evaluations</Title>

      <div style={{ 
        background: '#fff', 
        padding: 16, 
        borderRadius: 8, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.09)' 
      }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="_id"
          pagination={tableParams.pagination}
          onChange={handleTableChange}
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="middle"
          locale={{
            emptyText: <Empty description="No interview data available" />
          }}
        />
      </div>

      <Drawer
        title={
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Title level={4}>Interview Details</Title>
            {selectedRecord && (
              <Text type="secondary">
                {selectedRecord.enrollmentId?.userId?.name || 'Unknown Candidate'}
              </Text>
            )}
          </Space>
        }
        placement="right"
        width={window.innerWidth > 768 ? 600 : '90%'}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        extra={
          selectedRecord && (
            <Space>
              {getStatusTag(selectedRecord.enrollmentId?.progress?.interview?.status)}
              <Tag color="blue">
                Score: {formatScore(selectedRecord.enrollmentId?.progress?.interview?.score)}
              </Tag>
            </Space>
          )
        }
      >
        {selectedRecord && (
          <div style={{ marginBottom: 24 }}>
            <Collapse 
              defaultActiveKey={['feedback']} 
              expandIconPosition="end"
              style={{ marginBottom: 24 }}
            >
              <Panel header={<Text strong><FileTextOutlined /> Feedback</Text>} key="feedback">
                <div style={{ 
                  padding: 16, 
                  background: '#f9f9f9', 
                  borderRadius: 4 
                }}>
                  {formatFeedback(selectedRecord.enrollmentId?.progress?.interview?.feedback) || (
                    <Text type="secondary">No feedback available</Text>
                  )}
                </div>
              </Panel>
              <Panel header={<Text strong><FileTextOutlined /> Improvements</Text>} key="improvement">
                <div style={{ 
                  padding: 16, 
                  background: '#f9f9f9', 
                  borderRadius: 4 
                }}>
                  {formatFeedback(selectedRecord.enrollmentId?.progress?.interview?.improvement) || (
                    <Text type="secondary">No improvement suggestions available</Text>
                  )}
                </div>
              </Panel>
            </Collapse>
          </div>
        )}

        <Title level={5} style={{ margin: '16px 0' }}>
          <SoundOutlined /> Interview Questions & Responses
        </Title>

        {selectedInterview?.length > 0 ? (
          <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <Collapse defaultActiveKey={['0']} expandIconPosition="end">
              {selectedInterview.map((item, index) => (
                <Panel 
                  header={
                    <Text strong>{`${index + 1}. ${item.domain || 'Question'}`}</Text>
                  } 
                  key={index}
                >
                  <div style={{ 
                    padding: 16, 
                    background: '#f5f5f5', 
                    borderRadius: 8,
                    marginBottom: 16
                  }}>
                    <Paragraph>
                      <Text strong>Question: </Text>
                      {item.question}
                    </Paragraph>
                  </div>
                  
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Response Audio:</Text>
                    <div style={{ 
                      background: '#f0f8ff', 
                      padding: 12, 
                      borderRadius: 8,
                      marginTop: 8
                    }}>
                      {item.audio ? (
                        <audio 
                          controls 
                          src={item.audio} 
                          style={{ width: '100%' }} 
                          preload="none"
                        />
                      ) : (
                        <Text type="secondary">No audio available</Text>
                      )}
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          </div>
        ) : (
          <Empty description="No interview questions available" />
        )}
      </Drawer>
    </div>
  );
};

export default InterviewTable;